import { apiDelete, apiGet, apiPost } from './client';
import type { AgentTrace } from '@/lib/chatTypes';

export interface AdminTenant {
  id: string;
  slug: string;
  plan: string;
  status: string;
  created_at: string;
  owner_email: string;
  onboarding_complete: boolean;
}

export interface AdminTenantDetail extends AdminTenant {
  mls_id: string | null;
}

export interface AdminSitesResponse {
  tenants: AdminTenant[];
  total: number;
  page: number;
  limit: number;
}

export async function getAdminSites(params?: {
  page?: number;
  limit?: number;
  search?: string;
  plan?: string;
  status?: string;
}): Promise<AdminSitesResponse> {
  return apiGet<AdminSitesResponse>('/admin/sites', params as Record<string, unknown>);
}

export interface AdminSiteStats {
  ai_usage: Array<{
    id: string;
    feature: string;
    tokens_used: number;
    cost_usd: number;
    admin_used?: number;
    model_used?: string | null;
    created_at: string;
  }>;
  listings: {
    count: number;
    last_synced: string | null;
  };
}

// ── AI spend reporting (admin) ─────────────────────────────────────────────────

export interface AdminAiUsageTotals {
  customer_usd: number;
  staff_assisted_usd: number;
  total_usd: number;
  call_count: number;
  /** Number of tenants whose customer-billable usage exceeded their plan limit (tripwire). */
  over_limit_count?: number;
}

export interface AdminAiUsageTenantRow {
  tenant_id: string;
  slug: string;
  plan: string;
  owner_email: string | null;
  customer_usd: number;
  staff_assisted_usd: number;
  total_usd: number;
  call_count: number;
  /** Monthly AI credit for this tenant's plan (USD). */
  ai_limit_usd?: number;
  /** True when customer-billable usage exceeded the plan limit — a bug now that enforcement is live. */
  over_limit?: boolean;
}

export interface AdminAiUsageOverview {
  month: string;
  tenants: AdminAiUsageTenantRow[];
  totals: AdminAiUsageTotals;
}

/** Cross-customer AI spend for a month (default current). Customer-billable vs staff-assisted. */
export async function getAdminAiUsageOverview(month?: string): Promise<AdminAiUsageOverview> {
  return apiGet<AdminAiUsageOverview>('/admin/ai-usage/overview', month ? { month } : undefined);
}

export interface AdminAiUsageBreakdownRow {
  feature?: string;
  user_id?: string | null;
  email?: string | null;
  customer_usd: number;
  staff_assisted_usd: number;
  total_usd: number;
  calls: number;
}

export interface AdminAiUsageTenantDetail {
  tenant: { id: string; slug: string; plan: string; owner_email: string | null };
  month: string;
  totals: AdminAiUsageTotals;
  by_feature: AdminAiUsageBreakdownRow[];
  by_user: AdminAiUsageBreakdownRow[];
}

/** One customer's monthly AI spend, broken down by feature + user, with staff-assisted split. */
export async function getAdminAiUsageTenant(slug: string, month?: string): Promise<AdminAiUsageTenantDetail> {
  return apiGet<AdminAiUsageTenantDetail>(`/admin/ai-usage/tenant/${slug}`, month ? { month } : undefined);
}

export async function getAdminSite(slug: string): Promise<{ tenant: AdminTenantDetail }> {
  return apiGet<{ tenant: AdminTenantDetail }>(`/admin/sites/${slug}`);
}

export async function getAdminSiteStats(slug: string): Promise<AdminSiteStats> {
  return apiGet<AdminSiteStats>(`/admin/sites/${slug}/stats`);
}

export async function adminForceSync(slug: string): Promise<{ status: string; tenant_slug: string }> {
  return apiPost<{ status: string; tenant_slug: string }>(`/admin/sites/${slug}/sync`);
}

/**
 * Start a cookie-based impersonation session (Wave 2.4). The gateway sets the
 * distinct __Host-rs_impersonation + _csrf cookies (1h, bound to the admin's
 * session) and returns only the impersonated tenant slug — no token in JS. The
 * caller flips the auth store into impersonation mode and re-hydrates from /auth/me.
 */
export async function adminImpersonateStart(slug: string): Promise<{ ok: boolean; tenant_slug: string; expires_in: number }> {
  return apiPost<{ ok: boolean; tenant_slug: string; expires_in: number }>('/admin/impersonate/start', { slug });
}

/** Stop impersonating — clears only the impersonation cookies; admin session stays. */
export async function adminImpersonateStop(): Promise<{ ok: boolean }> {
  return apiPost<{ ok: boolean }>('/admin/impersonate/stop');
}

// ── Failed payments (dunning) ───────────────────────────────────────────────────
export interface FailedPaymentAccount {
  wallet_tenant_id: string;
  email: string | null;
  name: string | null;
  tenant_slug: string | null;
  tenant_id: string | null;
  plan: string | null;
  stripe_customer_id: string | null;
  dunning_state: 'grace' | 'suspended';
  first_failed_at: string | null;
  suspended_at: string | null;
  last_stage: string | null;
  last_email_at: string | null;
  days_in_dunning: number;
  days_until_suspension: number;
}
export interface FailedPaymentsResponse {
  generatedAt: string;
  grace: number;
  suspended: number;
  accounts: FailedPaymentAccount[];
}
export async function getFailedPayments(): Promise<FailedPaymentsResponse> {
  return apiGet<FailedPaymentsResponse>('/billing/admin/failed-payments');
}
export async function resendFailedPaymentWarning(walletTenantId: string): Promise<{ sent: boolean }> {
  return apiPost<{ sent: boolean }>(`/billing/admin/failed-payments/${encodeURIComponent(walletTenantId)}/resend`, {});
}
export async function clearFailedPayment(walletTenantId: string): Promise<{ cleared: boolean }> {
  return apiPost<{ cleared: boolean }>(`/billing/admin/failed-payments/${encodeURIComponent(walletTenantId)}/clear`, {});
}


// ── Admin → Payments → one account (subscription, cards, invoices) ─────────────────────────

export interface BillingAccountSummary {
  user_id: string;
  email: string | null;
  name: string | null;
  tenant_slug: string | null;
  plan: string | null;
  customer_id: string | null;
}
export interface BillingAccount extends BillingAccountSummary {
  tenant_id: string | null;
  dunning_state: string | null;
}
export interface AdminSubscription {
  id: string;
  status: string;
  price_id: string | null;
  product_id: string | null;
  nickname: string | null;
  amount_cents: number | null;
  currency: string;
  interval: string | null;
  interval_count: number;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  ended_at: string | null;
}
export interface AdminPaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
}
export interface AdminInvoice {
  id: string;
  number: string | null;
  status: string | null;
  created: string;
  period_start: string | null;
  period_end: string | null;
  amount_due_cents: number;
  amount_paid_cents: number;
  currency: string;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  description: string | null;
  /** Raised from New charge (a setup fee, quoted work) — these get Collect now. */
  custom_charge?: boolean;
}
/** A custom charge waiting to ride on the next subscription bill. */
export interface PendingCharge {
  id: string;
  description: string | null;
  amount_cents: number;
  currency: string;
  created: string;
  category: string | null;
}
export type CustomChargeCategory = 'setup' | 'custom_work' | 'other';
export type CustomChargeCollection = 'send' | 'charge' | 'next_bill';
export interface CustomChargeInput {
  description: string;
  amount_cents: number;
  collection: CustomChargeCollection;
  category: CustomChargeCategory;
  days_until_due?: number;
  /** Staff-only, for the audit log; never sent to Stripe. */
  note?: string;
  /** One per dialog opening, so a retried request never charges twice. */
  idempotency_key: string;
}
export type PriceCatalog = Record<string, { monthly: string | null; annual: string | null }>;
export interface AccountBilling {
  account: BillingAccount;
  subscription: AdminSubscription | null;
  payment_methods: AdminPaymentMethod[];
  default_payment_method_id: string | null;
  invoices: AdminInvoice[];
  open_invoice: AdminInvoice | null;
  pending_charges?: PendingCharge[];
  catalog: PriceCatalog;
  generatedAt: string;
}

const acct = (userId: string) => `/billing/admin/accounts/${encodeURIComponent(userId)}`;

export async function searchBillingAccounts(q: string): Promise<{ accounts: BillingAccountSummary[] }> {
  return apiGet<{ accounts: BillingAccountSummary[] }>('/billing/admin/accounts', { q });
}
export async function getAccountBilling(userId: string): Promise<AccountBilling> {
  return apiGet<AccountBilling>(`${acct(userId)}/billing`);
}
export async function retryAccountPayment(userId: string): Promise<{ paid: boolean; invoice_id: string; amount_cents: number }> {
  return apiPost(`${acct(userId)}/retry-payment`, {});
}
export async function setAccountDefaultCard(userId: string, pmId: string): Promise<{ default_payment_method_id: string }> {
  return apiPost(`${acct(userId)}/payment-methods/${encodeURIComponent(pmId)}/default`, {});
}
export async function removeAccountCard(userId: string, pmId: string): Promise<{ detached: boolean }> {
  return apiDelete(`${acct(userId)}/payment-methods/${encodeURIComponent(pmId)}`);
}
export async function requestAccountCard(userId: string, note: string): Promise<{ sent: boolean; to: string }> {
  return apiPost(`${acct(userId)}/request-card`, { note });
}
export async function resumeAccountSubscription(userId: string, priceId: string | null): Promise<{ outcome: 'resumed' | 'restarted' | 'already_active'; subscription: AdminSubscription; plan: string | null }> {
  return apiPost(`${acct(userId)}/subscription/resume`, priceId ? { price_id: priceId } : {});
}
export async function cancelAccountSubscription(userId: string, when: 'period_end' | 'now'): Promise<{ outcome: 'scheduled' | 'cancelled'; subscription: AdminSubscription; plan: string | null }> {
  return apiPost(`${acct(userId)}/subscription/cancel`, { when });
}
export async function changeAccountSubscription(
  userId: string,
  change: { price_id: string } | { amount_cents: number },
  proration: 'create_prorations' | 'none',
): Promise<{ subscription: AdminSubscription; created_price_id: string | null; plan: string | null }> {
  return apiPost(`${acct(userId)}/subscription/change`, { ...change, proration });
}
export async function createAccountInvoice(
  userId: string,
  input: CustomChargeInput,
): Promise<{ invoice: AdminInvoice | null; pending: PendingCharge | null; charged: boolean; warning: string | null }> {
  return apiPost(`${acct(userId)}/invoices`, input);
}
export async function collectAccountCharge(userId: string, invoiceId: string): Promise<{ invoice: AdminInvoice; paid: boolean }> {
  return apiPost(`${acct(userId)}/invoices/${encodeURIComponent(invoiceId)}/collect`, {});
}
export async function removeAccountPendingCharge(userId: string, itemId: string): Promise<{ removed: boolean }> {
  return apiDelete(`${acct(userId)}/pending-charges/${encodeURIComponent(itemId)}`);
}
export async function voidAccountInvoice(userId: string, invoiceId: string): Promise<{ voided: boolean }> {
  return apiDelete(`${acct(userId)}/invoices/${encodeURIComponent(invoiceId)}`);
}

// ── Chat dead ends: the turns that ended with nothing to click ─────────────────

export interface ChatDeadEnd {
  id: string;
  site_id: string;
  tenant_slug: string | null;
  prompt: string;
  reply: string;
  ok: boolean | null;
  /** Plan C2 — 'down' = the agent said this reply didn't work (even though it reported ok). */
  feedback?: 'up' | 'down' | null;
  /** Plan C1 — what the agent loop did for this reply; null for a non-agent reply or an old row. */
  agent_trace?: AgentTrace | null;
  created_at: string;
}

/** Why a BLT turn is on the page (api-gateway lib/assistant-dead-ends.ts). */
export type BltDeadEndSignal = 'thumbs_down' | 'false_claim' | 'failed' | 'refused' | 'undone' | 'approval_expired' | 'corrected';

export interface BltLedgerRow {
  name: string;
  status: string;
  reason?: string | null;
}

/** A BLT turn that went wrong: what the owner asked, what BLT wrote, what they saw, what the ledger did. */
export interface BltDeadEnd {
  run_id: string;
  site_id: string;
  tenant_slug: string | null;
  prompt: string;
  /** What BLT first wrote, when the owner was shown something else. */
  first_reply: string | null;
  shown_reply: string;
  tools: BltLedgerRow[];
  /** Refusals the turn never got past (false starts it got past are left out). */
  refused: BltLedgerRow[];
  signals: BltDeadEndSignal[];
  /** The owner's next message, when it read as a correction. */
  correction: string | null;
  feedback_note: string | null;
  created_at: string;
}

export async function getChatDeadEnds(days = 7): Promise<{
  days: number;
  dead_ends: ChatDeadEnd[];
  /** Absent from gateways older than the BLT rows. */
  blt_dead_ends?: BltDeadEnd[];
  /** False until BLT_DEAD_ENDS_HONESTY is on: caught false claims are not counted yet. */
  blt_honesty_signal?: boolean;
  /** True when the window held more BLT turns than the gateway reads; the oldest were left out. */
  blt_read_capped?: boolean;
}> {
  return apiGet('/admin/chat-log/dead-ends', { days });
}
